import type { RideAnalytics, RideRecord } from "../types/ride";
import { computeAerobicDecoupling } from "./aerobicDecoupling";
import { detectIntervals } from "./intervalDetection";
import { classifyWorkout } from "./workoutClassification";

const NORMALIZED_POWER_WINDOW_SECONDS = 30;
const LONG_GAP_MULTIPLIER = 5;

const COGGAN_POWER_ZONES = [
  { zoneName: "Zone 1", upperFtpFraction: 0.55 },
  { zoneName: "Zone 2", upperFtpFraction: 0.75 },
  { zoneName: "Zone 3", upperFtpFraction: 0.9 },
  { zoneName: "Zone 4", upperFtpFraction: 1.05 },
  { zoneName: "Zone 5", upperFtpFraction: 1.2 },
  { zoneName: "Zone 6", upperFtpFraction: 1.5 },
  { zoneName: "Zone 7", upperFtpFraction: Number.POSITIVE_INFINITY }
];

type TimedRecord = {
  timeSeconds: number;
  powerWatts: number | null;
};

type TimelineSegment = {
  startSeconds: number;
  endSeconds: number;
  powerWatts: number;
  isLongGap: boolean;
};

type PowerTimeline = {
  totalDurationSeconds: number;
  validPowerDurationSeconds: number;
  totalEnergyJoules: number;
  energyBySecond: number[];
  segments: TimelineSegment[];
};

type PowerZoneResult = RideAnalytics["powerZones"][number];

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return null;
}

function toRecordTimeSeconds(record: RideRecord): number | null {
  const elapsedSeconds = toFiniteNumber(record.elapsedSeconds);
  if (elapsedSeconds !== null) {
    return elapsedSeconds;
  }

  if (record.timestamp) {
    const timestampMs = Date.parse(record.timestamp);
    if (!Number.isNaN(timestampMs)) {
      return timestampMs / 1000;
    }
  }

  return null;
}

function getMedian(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sortedValues = [...values].sort((left, right) => left - right);
  const middleIndex = Math.floor(sortedValues.length / 2);

  if (sortedValues.length % 2 === 1) {
    return sortedValues[middleIndex];
  }

  return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
}

function buildPowerTimeline(records: RideRecord[]): PowerTimeline | null {
  const timedRecords: TimedRecord[] = records
    .map(record => ({
      timeSeconds: toRecordTimeSeconds(record),
      powerWatts: toFiniteNumber(record.powerWatts)
    }))
    .filter(record => record.timeSeconds !== null)
    .map(record => ({
      timeSeconds: record.timeSeconds as number,
      powerWatts: record.powerWatts
    }))
    .sort((left, right) => left.timeSeconds - right.timeSeconds);

  if (timedRecords.length < 2) {
    return null;
  }

  const sampleIntervals = timedRecords
    .slice(1)
    .map((record, index) => record.timeSeconds - timedRecords[index].timeSeconds)
    .filter(interval => interval > 0);

  const medianIntervalSeconds = getMedian(sampleIntervals);
  if (medianIntervalSeconds === null || medianIntervalSeconds <= 0) {
    return null;
  }

  const firstTimestampSeconds = timedRecords[0].timeSeconds;
  const lastTimestampSeconds = timedRecords[timedRecords.length - 1].timeSeconds;
  const totalDurationSeconds = lastTimestampSeconds - firstTimestampSeconds;

  if (totalDurationSeconds <= 0) {
    return null;
  }

  const longGapThresholdSeconds = medianIntervalSeconds * LONG_GAP_MULTIPLIER;

  // Missing power policy:
  // - Missing/non-finite power samples are treated as 0 W for their segment.
  // - Large sampling gaps (> 5x median interval) are treated as no-power segments.
  // - Gaps are preserved in time (never compressed).
  const segments: TimelineSegment[] = [];
  for (let index = 0; index < timedRecords.length - 1; index += 1) {
    const current = timedRecords[index];
    const next = timedRecords[index + 1];

    const startSeconds = current.timeSeconds - firstTimestampSeconds;
    const endSeconds = next.timeSeconds - firstTimestampSeconds;
    const segmentDurationSeconds = endSeconds - startSeconds;

    if (segmentDurationSeconds <= 0) {
      continue;
    }

    const isLongGap = segmentDurationSeconds > longGapThresholdSeconds;
    const rawPowerWatts = current.powerWatts ?? 0;

    segments.push({
      startSeconds,
      endSeconds,
      powerWatts: isLongGap ? 0 : rawPowerWatts,
      isLongGap
    });
  }

  if (segments.length === 0) {
    return null;
  }

  const timelineLengthSeconds = Math.max(1, Math.ceil(totalDurationSeconds));
  const energyBySecond = new Array<number>(timelineLengthSeconds).fill(0);

  let totalEnergyJoules = 0;
  let validPowerDurationSeconds = 0;

  for (const segment of segments) {
    const segmentDurationSeconds = segment.endSeconds - segment.startSeconds;
    if (!segment.isLongGap) {
      validPowerDurationSeconds += segmentDurationSeconds;
    }

    totalEnergyJoules += segment.powerWatts * segmentDurationSeconds;

    const startSecond = Math.floor(segment.startSeconds);
    const endSecond = Math.ceil(segment.endSeconds) - 1;

    for (let second = startSecond; second <= endSecond; second += 1) {
      if (second < 0 || second >= energyBySecond.length) {
        continue;
      }

      const overlapStart = Math.max(segment.startSeconds, second);
      const overlapEnd = Math.min(segment.endSeconds, second + 1);
      const overlapDurationSeconds = overlapEnd - overlapStart;

      if (overlapDurationSeconds > 0) {
        energyBySecond[second] += segment.powerWatts * overlapDurationSeconds;
      }
    }
  }

  return {
    totalDurationSeconds,
    validPowerDurationSeconds,
    totalEnergyJoules,
    energyBySecond,
    segments
  };
}

function normalizeFtp(ftpInput: number | null | undefined): number | null {
  if (typeof ftpInput !== "number" || !Number.isFinite(ftpInput) || ftpInput <= 0) {
    return null;
  }

  return ftpInput;
}

function computeNormalizedPower(energyBySecond: number[], validPowerDurationSeconds: number): number | null {
  if (validPowerDurationSeconds < NORMALIZED_POWER_WINDOW_SECONDS) {
    return null;
  }

  if (energyBySecond.length < NORMALIZED_POWER_WINDOW_SECONDS) {
    return null;
  }

  const prefixEnergy = new Array<number>(energyBySecond.length + 1).fill(0);
  for (let index = 0; index < energyBySecond.length; index += 1) {
    prefixEnergy[index + 1] = prefixEnergy[index] + energyBySecond[index];
  }

  const rollingFourthPowers: number[] = [];
  const maxWindowStart = energyBySecond.length - NORMALIZED_POWER_WINDOW_SECONDS;

  for (let windowStart = 0; windowStart <= maxWindowStart; windowStart += 1) {
    const windowEnd = windowStart + NORMALIZED_POWER_WINDOW_SECONDS;
    const windowEnergyJoules = prefixEnergy[windowEnd] - prefixEnergy[windowStart];
    const rollingAveragePower = windowEnergyJoules / NORMALIZED_POWER_WINDOW_SECONDS;
    rollingFourthPowers.push(rollingAveragePower ** 4);
  }

  if (rollingFourthPowers.length === 0) {
    return null;
  }

  const meanFourthPower = rollingFourthPowers.reduce((sum, value) => sum + value, 0) / rollingFourthPowers.length;
  return meanFourthPower > 0 ? meanFourthPower ** 0.25 : 0;
}

function createUnavailableZoneResults(): PowerZoneResult[] {
  return COGGAN_POWER_ZONES.map(zone => ({
    zoneName: zone.zoneName,
    seconds: null,
    percentageOfMovingTime: null
  }));
}

function resolveCogganZoneIndex(powerToFtpRatio: number): number {
  for (let zoneIndex = 0; zoneIndex < COGGAN_POWER_ZONES.length; zoneIndex += 1) {
    if (powerToFtpRatio <= COGGAN_POWER_ZONES[zoneIndex].upperFtpFraction) {
      return zoneIndex;
    }
  }

  return COGGAN_POWER_ZONES.length - 1;
}

function computePowerZoneResults(timeline: PowerTimeline, ftpUsed: number | null, movingTimeSeconds: number | null | undefined): PowerZoneResult[] {
  if (ftpUsed === null) {
    return createUnavailableZoneResults();
  }

  const zoneSeconds = new Array<number>(COGGAN_POWER_ZONES.length).fill(0);
  for (const segment of timeline.segments) {
    if (segment.isLongGap) {
      continue;
    }

    const segmentDurationSeconds = segment.endSeconds - segment.startSeconds;
    if (segmentDurationSeconds <= 0) {
      continue;
    }

    const powerToFtpRatio = segment.powerWatts / ftpUsed;
    const zoneIndex = resolveCogganZoneIndex(powerToFtpRatio);
    zoneSeconds[zoneIndex] += segmentDurationSeconds;
  }

  const movingTimeDenominatorSeconds =
    typeof movingTimeSeconds === "number" && Number.isFinite(movingTimeSeconds) && movingTimeSeconds > 0
      ? movingTimeSeconds
      : timeline.validPowerDurationSeconds;

  return COGGAN_POWER_ZONES.map((zone, zoneIndex) => ({
    zoneName: zone.zoneName,
    seconds: zoneSeconds[zoneIndex],
    percentageOfMovingTime:
      movingTimeDenominatorSeconds > 0
        ? (zoneSeconds[zoneIndex] / movingTimeDenominatorSeconds) * 100
        : null
  }));
}

export function computeRideAnalytics(
  records: RideRecord[],
  ftpInput: number | null | undefined,
  movingTimeSeconds: number | null | undefined = null,
  recordingIntervalSeconds: number | null | undefined = null
): RideAnalytics {
  const timeline = buildPowerTimeline(records);
  const ftpUsed = normalizeFtp(ftpInput);
  const aerobicDecoupling = computeAerobicDecoupling(records, recordingIntervalSeconds);
  const intervalDetection = detectIntervals(records, ftpInput);

  if (timeline === null || timeline.validPowerDurationSeconds <= 0) {
    const baseAnalytics = {
      normalizedPower: null,
      intensityFactor: null,
      variabilityIndex: null,
      trainingStressScore: null,
      averagePower: null,
      workKilojoules: null,
      validPowerDurationSeconds: null,
      ftpUsed,
      powerZones: createUnavailableZoneResults(),
      aerobicDecoupling,
      intervalDetection
    };
    const workoutClassification = classifyWorkout(intervalDetection, baseAnalytics, ftpInput);

    return {
      ...baseAnalytics,
      workoutClassification
    };
  }

  const averagePower = timeline.totalEnergyJoules / timeline.validPowerDurationSeconds;
  const workKilojoules = timeline.totalEnergyJoules / 1000;
  const normalizedPower = computeNormalizedPower(timeline.energyBySecond, timeline.validPowerDurationSeconds);

  const intensityFactor = ftpUsed !== null && normalizedPower !== null ? normalizedPower / ftpUsed : null;
  const variabilityIndex = normalizedPower !== null && averagePower > 0 ? normalizedPower / averagePower : null;

  const trainingStressScore =
    ftpUsed !== null && normalizedPower !== null && intensityFactor !== null
      ? (timeline.validPowerDurationSeconds * normalizedPower * intensityFactor) / (ftpUsed * 3600) * 100
      : null;

  const powerZones = computePowerZoneResults(timeline, ftpUsed, movingTimeSeconds);

  const baseAnalytics = {
    normalizedPower,
    intensityFactor,
    variabilityIndex,
    trainingStressScore,
    averagePower,
    workKilojoules,
    validPowerDurationSeconds: timeline.validPowerDurationSeconds,
    ftpUsed,
    powerZones,
    aerobicDecoupling,
    intervalDetection
  };

  const workoutClassification = classifyWorkout(intervalDetection, baseAnalytics, ftpInput);

  return {
    ...baseAnalytics,
    workoutClassification
  };
}
